/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { REQUESTED_DEVICES_FEATURE_NAME } from '../../../../constants';

export interface Page<T> {
  data: T[];
  count: number;
}

// The Home dashboard's endpoints, ported from the AngularJS UserService, UserDeviceService, FeedbackService and
// ActivityService calls the legacy dashboard cards made.
@Injectable({ providedIn: 'root' })
export class DashboardService {
  constructor(private http: HttpClient) {}

  projects(): Promise<Page<any>> {
    return firstValueFrom(this.http.get<Page<any>>('/api/v1/users/me/projects'));
  }

  invitations(): Promise<Page<any>> {
    return firstValueFrom(this.http.get<Page<any>>('/api/v1/users/me/invitations'));
  }

  devices(): Promise<any[]> {
    return firstValueFrom(this.http.get<any[]>('/api/v1/users/me/devices'));
  }

  requestedDevices(): Promise<any[]> {
    return firstValueFrom(this.http.get<any[]>('/api/v1/home/requested_devices'));
  }

  // Resolves to null (204 No Content) when the tester hasn't answered yet.
  requestedDevicesFeedback(): Promise<{ is_positive: boolean } | null> {
    return firstValueFrom(this.http.get<{ is_positive: boolean } | null>('/api/v1/feedback', { params: { feature: REQUESTED_DEVICES_FEATURE_NAME } }));
  }

  sendRequestedDevicesFeedback(isPositive: boolean): Promise<unknown> {
    return firstValueFrom(this.http.post('/api/v1/feedback', { feature: REQUESTED_DEVICES_FEATURE_NAME, is_positive: isPositive }));
  }

  clearProjectActivities(): Promise<unknown> {
    return firstValueFrom(this.http.put('/api/v1/activities/clear_projects', ''));
  }

  clearCommunityActivities(): Promise<unknown> {
    return firstValueFrom(this.http.put('/api/v1/activities/clear_community', ''));
  }
}
